/**
 * Demo Guide jargon — keep technical terms, explain on hover.
 * Longest-match-first so "Mixer (diamond)" wins over "Mixer".
 *
 * Terms that name a place in the UI also carry a uiLocations key, so hover
 * answers "what is this?" and click answers "where is it?". Attaching the
 * pointer here rather than to individual demos is what makes all 51 walkthroughs
 * navigable at once: DemoGuide already runs every howTo step, description, and
 * "Where to find features" bullet through this matcher.
 */
import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Crosshair } from 'lucide-react';
import type { UiLocationKey } from './tour/uiLocations';
import { JARGON_LOCATIONS } from './tour/jargonLocations';
import { locateUi } from './tour/uiLocator';

/** Phrase → short plain-English explainer (shown on hover). */
export const DEMO_JARGON: Record<string, string> = {
  // ── UI / layout ──────────────────────────────────────────────
  'Node Inspector':
    'The right-hand panel. Click any wallet on the map to see its details, tabs, and chips here.',
  'Inspector chips':
    'Small tags in the Node Inspector (right panel) that show role, entity, risk, etc.',
  chips:
    'Small coloured tags in the Node Inspector. They carry extra detail (hot wallet, deposit, Bridge…) that is not painted as a border colour on the map.',
  canvas:
    'The investigation map in the centre of the screen — wallets are boxes, transfers are lines between them.',
  'round-rectangle':
    'The default wallet shape on the map: a rounded box. Most addresses look like this until they are high-danger (mixer/scam).',
  'round rectangle':
    'The default wallet shape on the map: a rounded box. Most addresses look like this until they are high-danger (mixer/scam).',
  subcategory:
    'The wallet’s role within a bigger category — e.g. hot wallet, cold storage, or deposit under an exchange. Shown as Inspector chips, not as coloured borders.',
  'Triage Ribbon':
    'Quick status chips (risk, sanctions, bridge, mixed…) shown for the selected wallet so you can triage in seconds.',
  Omnibar:
    'The search bar at the top where you paste an address, UPI ID, or transaction hash.',
  'Fund Flow Playback':
    'Replay button (▶) that animates transfers in time order so you can watch money move hop by hop.',
  'Pattern Scan':
    'Analysis tool that auto-flags known crime patterns (peel chain, smurfing, pig butchering, etc.).',
  'AI Summary':
    'Plain-language narrative of the investigation generated from the graph and findings.',
  'Auto-Trace':
    'Finds a short path from the selected wallet to a known exchange — like GPS for stolen funds.',
  'Find Links':
    'Checks whether wallets already on the canvas are connected through on-chain paths.',
  'Gold Path':
    'Gold-highlighted trail on the map showing the money route Auto-Trace found to an exchange.',
  'Case wallets':
    'List of wallets already on the open case. Click one to select it on the canvas, or add it from the case graph — no extra fetch.',
  'Promote to case':
    'Writes the finished Targeted Node Intelligence run onto the open case. Disabled with a reason when the Workbench was not opened from a case.',
  // ── Fusion Canvas ────────────────────────────────────────────
  'Clear focus':
    'Restores every wallet to full visibility on the evidence canvas after a consolidated bucket has dimmed the rest.',
  Palette:
    'Left rail on Fusion Canvas — Evidence In, Operators, Algorithms, and Playbooks. Collapse it with the edge tab for a wider graph.',
  'Court-safe':
    'Top-bar toggle that dims scored (inferred) links and leaves only deterministic joins (UTR, tx hash, registry).',
  'Court-safe Chain':
    'Right-rail tab listing proven joins by tier, including bank↔crypto handover seams.',
  'Surface filters':
    'Legend list that shows or hides crypto, bank, social, company, and network modules on the Fusion graph.',
  Timeline:
    'Same as Ingestion Timeline — right-side dot on Fusion Canvas; click to expand Play / scrub stages.',
  'Ingestion Timeline':
    'Collapses to a stage-colored dot on the right of the Fusion canvas (label: Ingestion Timeline). Click to expand the bottom Play stages bar. Not transfer playback, and not the Run rail.',
  'Ingestion timeline':
    'Same as Ingestion Timeline — right-side dot; click to expand Play stages.',
  'Play stages':
    'Primary control on the expanded Ingestion Timeline — advances case assembly stage by stage. Pause, step, or scrub the dots anytime.',
  'Fusion Inspector':
    'Right rail on Fusion — provenance, tier, source, and activity for the selected node or edge.',
  'Bank import':
    'Inspector tab that uploads a bank statement into StatementFlow, auto-detects the account holder, and drops parsed rows onto the Fusion canvas (config-gated).',
  'Case files':
    'Fusion right-rail dossier after bank import — holder, account number, transactions, counterparties, debits and credits. Promote from demo sandbox into a real LE case.',
  'New case':
    'Creates a named LE case (same fields as Cases) and opens Case Canvas for real evidence — separate from the Fusion demo sandbox. Also on Build → Create a new case.',
  'Open case':
    'Fusion sidebar: pick an existing LE case and open it straight on its Case Canvas (/cases/:id/canvas). From Cases or the Dashboard a case opens on its file page instead, where Open Fusion Canvas (or the sparkle control on the case card) opens the canvas.',
  'Create a new case':
    'Build-tab path for live work — names an LE case and opens Case Canvas with no demo seed.',
  'Explore demos':
    'Build-tab path for training — opens the demo library and synthetic sandbox; never a live FIR case.',
  'Build tab':
    'Fusion top tab — first choose Explore demos or Create a new case, then author evidence cards.',
  'Journal tab':
    'Fusion top tab — append-only build history. Replay all streams runs on Canvas; Up to here rolls later nodes back; From here plays forward.',
  'Run rail':
    'Strip under the Fusion canvas. Every operator, evidence card, and Autopilot act posts a job chip here. Click a chip to jump to its node. Separate from Play on the timeline.',
  'Radial menu':
    'Right-click ring on a Fusion node — only operators valid for that entity type.',
  Autopilot:
    'Palette → Playbooks script that walks the case, focuses nodes, posts Run rail jobs, and may flip Court-safe.',
  'Edge Actions':
    'Right-click menu on a transfer line — Emphasize, De-emphasize, Focus, and related tools.',
  Emphasize:
    'Highlights a transfer line so key evidence stands out on a busy map.',
  Focus:
    'Hides everything except the edges/nodes you selected — like isolating CCTV frames.',

  // ── Tabs ─────────────────────────────────────────────────────
  'Intel tab':
    'Intelligence tab in the Node Inspector — sanctions, profile, exposure, bridges, DEX, privacy checks.',
  'Intelligence tab':
    'Same as Intel: risk and identity checks for the selected wallet (sanctions, profile, exposure, bridges…).',
  'Analysis tab':
    'Node Inspector tab for investigation tools — Auto-Investigate, AI Summary, Pattern Scan (shortcuts G / I).',
  'Transfers tab':
    'Lists money movements for the selected wallet; choose a count and click Load to fetch more onto the map.',
  'Activity tab':
    'Older name for the Transfers tab — same transaction history and Load controls.',
  'Actions tab':
    'Flag, export, legal notice, and other case actions for the selected wallet.',
  'Overview tab':
    'Summary card for the selected node — identity, balances, and quick facts.',
  'Parties tab':
    'Lists every wallet this address sent to or received from, grouped by known entity.',
  'Forensic tab':
    'Records an exchange disclosed under subpoena for this wallet — KYC leads and confirmed movements.',

  // ── Canvas chrome ────────────────────────────────────────────────
  'Follow the Money':
    'Traces the selected wallet to the nearest exchange and paints the route gold. Available from the header Trace button, the Analysis tab, and the Trace icon row.',
  Legend:
    'The colour / shape key for the map — what gold, purple-dashed, diamonds and triangles mean. Opens from the header menu.',
  minimap:
    'Small overview of the whole graph in the bottom-right corner; click it to jump. Appears once the canvas passes 30 nodes.',
  Recompute:
    'Re-lays out the canvas and drops hidden nodes so a sprawling map becomes compact. Never changes the underlying data.',
  'Rearrange as':
    'Bottom canvas strip — Smart (auto-picks), Flow (L→R money path), Organic (dense/cycles), Radial (hub-and-spoke). Moves nodes only; never changes evidence.',
  'graph filters':
    'Narrows what the canvas shows by value, risk, chain, or date. Filtered nodes dim rather than disappear.',
  'Undo/Redo':
    'Steps backward or forward through canvas changes (Ctrl+Z / Ctrl+Shift+Z) — expansions, deletions, and layout runs.',
  'LE Report':
    'The court-facing HTML/PDF report built from an investigation, with Section 65B citations.',

  // ── Shapes / visual key ──────────────────────────────────────
  'Mixer (diamond)':
    'Diamond shape = mixer / tumbler — a service that mixes coins to hide where money came from.',
  'Scam (triangle)':
    'Triangle shape = scam or mule-style high-danger wallet.',
  'dashed blue border':
    'Dashed blue outline = smart contract (a program on the blockchain), not a normal personal wallet.',
  'Attribution Hub':
    'Pink dashed octagon grouping related wallets (e.g. several Binance deposits) linked by common attribution.',
  'smart contract':
    'Code that lives on the blockchain and can hold or move funds automatically — shown with a dashed blue border.',
  'hot wallet':
    'Exchange wallet kept online for day-to-day deposits and withdrawals (higher operational risk than cold storage).',
  'cold storage':
    'Offline / vault-style exchange wallet used for long-term holdings — lower day-to-day exposure.',
  'Cold Storage':
    'Offline / vault-style exchange wallet used for long-term holdings — lower day-to-day exposure.',
  deposit:
    'A wallet customers send funds into (often one per user) before the exchange sweeps them inward.',
  OTC:
    'Over-the-counter desk — large private trades outside the public order book.',

  // ── Crypto / LE concepts ─────────────────────────────────────
  Mixer:
    'A service that mixes many people’s crypto to hide the origin — like cash in a shared pot.',
  Tumbler:
    'Another word for mixer — mixes coins so deposits and withdrawals are hard to link 1:1.',
  'Peel Chain':
    'Laundering pattern: send slightly smaller amounts out to many fresh wallets, like peeling an onion.',
  'peel chain':
    'Laundering pattern: send slightly smaller amounts out to many fresh wallets, like peeling an onion.',
  DEX:
    'Decentralised exchange (e.g. Uniswap) — swaps tokens with no company KYC in the middle.',
  'Decentralized Exchange':
    'Exchange with no central operator — trades via smart contracts; often used to layer stolen funds.',
  Exposure:
    'Who this wallet has dealt with. High mixer or sanctioned exposure is a red flag.',
  Clustering:
    'Grouping wallets that likely belong to the same person (shared funder, timing, deposit reuse…).',
  Counterparty:
    'The other side of a transfer — who this wallet sent to or received from.',
  counterparties:
    'The other wallets this address has transacted with.',
  Smurfing:
    'Breaking a large sum into many small transfers just under reporting thresholds (structuring).',
  Structuring:
    'Same idea as smurfing — many small transfers to stay under reporting limits.',
  'Pig Butchering':
    'Romance / fake-investment scam: build trust, take rising deposits, then drain and cash out.',
  'pig butchering':
    'Romance / fake-investment scam: build trust, take rising deposits, then drain and cash out.',
  'Whale Alert':
    'Unusually large transfer (often $1M+) — hard to miss, often hack or laundering cash-out.',
  'Dormant Activation':
    'A long-quiet wallet suddenly moves large funds — like a closed account emptying overnight.',
  Honeypot:
    'Scam contract that accepts deposits but blocks withdrawals — money goes in, not out.',
  'Fan-In':
    'Many wallets sending into one collector (often a money mule) before cash-out.',
  'fan-in':
    'Many wallets sending into one collector (often a money mule) before cash-out.',
  'Fan-Out':
    'One wallet spreading funds out to many recipients — common in peel / structuring patterns.',
  'fan-out':
    'One wallet spreading funds out to many recipients — common in peel / structuring patterns.',
  'Money Mule':
    'Person or wallet that receives criminal funds and forwards them — like a cash courier.',
  mule:
    'Wallet or person used to receive and forward criminal funds toward an exchange cash-out.',
  OFAC:
    'US Treasury sanctions list. Dealing with listed wallets is illegal in many jurisdictions; we auto-flag hits.',
  'OFAC SDN':
    'OFAC Specially Designated Nationals list — the live sanctions database we screen against.',
  sanctions:
    'Government ban list (e.g. OFAC). Sanctioned wallets and mixers get strong red warnings.',
  Bridge:
    'Service that moves crypto from one blockchain to another (e.g. Ethereum → Polygon) — common hop to muddy the trail.',
  bridge:
    'Service that moves crypto from one blockchain to another — follow every hop to the cash-out.',
  CoinJoin:
    'Bitcoin privacy technique: many people mix equal amounts in one transaction so inputs and outputs are hard to link 1:1.',
  UTXO:
    'Unspent transaction output — Bitcoin’s “coin” model. CoinJoin detectors look at equal-valued UTXOs.',
  nonce:
    'Serial number on each Ethereum-style outgoing transfer. Gaps mean some transfers were cancelled or replaced.',
  'Nonce Gaps':
    'Missing serial numbers in the transfer sequence — cancelled or sped-up transactions; ask why.',
  EVM:
    'Ethereum Virtual Machine family of chains (ETH, BNB, Polygon…). Same tooling works across them.',
  'ERC-20':
    'Standard token type on Ethereum-style chains (USDT, USDC, etc.).',
  USDT:
    'Tether stablecoin — often the cash-out token in Indian fraud cases (especially on Tron).',
  TRC20:
    'Token standard on the Tron network — USDT-TRC20 is the dominant rail for India-facing P2P cash-outs.',
  Tron:
    'Blockchain network (TRX) widely used for USDT transfers in Indian crypto fraud cases.',
  UPI:
    'India’s real-time bank payment ID (e.g. name@paytm). Linking UPI to a crypto wallet bridges fiat identity to chain.',
  P2P:
    'Peer-to-peer trading — buyers/sellers meet off-exchange (Telegram, Binance P2P) and settle with UPI + crypto.',
  FIU:
    'Financial Intelligence Unit — India. FIU-registered exchanges are key freeze / notice targets.',
  FIR:
    'First Information Report — the police complaint that often lists suspect wallets or tx hashes.',
  KYC:
    'Know Your Customer identity checks. DEXs and mixers usually have none; regulated exchanges do.',
  CEX:
    'Centralised exchange (Binance, WazirX…) — company-run, with KYC; common final cash-out point.',
  DeFi:
    'Decentralised finance — lending, swaps, pools run by smart contracts instead of banks.',
  liquidity:
    'Funds locked in a pool so a token can be traded. Rug pulls drain this pool and leave buyers stuck.',
  'rug pull':
    'Exit scam: launch a token, take investor money, then drain the liquidity pool overnight.',
  laundering:
    'Moving stolen funds through hops (swaps, bridges, mixers) to hide the source before cash-out.',
  obfuscation:
    'Deliberately muddying the money trail — bridges, mixers, peel chains, multi-chain hops.',
  typology:
    'Named crime pattern the system detects (e.g. peel_chain, smurfing, pig_butchering).',
  heuristic:
    'Rule-of-thumb detector (e.g. equal outputs → CoinJoin) — strong clue, not absolute proof.',
  provenance:
    'Where funds came from before a mix or hop — the trail you still need for evidence.',
  'on-chain':
    'Visible on the public blockchain ledger (addresses, amounts, times) vs off-chain (bank, Telegram, device).',
  entity:
    'Named organisation or person we attributed to a wallet (Binance, WazirX, Tornado Cash…).',
};

type Segment = { text: string; explainer?: string; locate?: UiLocationKey };

let _sortedTerms: string[] | null = null;
let _regex: RegExp | null = null;

function getMatcher(): { terms: string[]; regex: RegExp } {
  if (_sortedTerms && _regex) return { terms: _sortedTerms, regex: _regex };
  _sortedTerms = Object.keys(DEMO_JARGON).sort((a, b) => b.length - a.length);
  const escaped = _sortedTerms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  // Word-ish boundaries so "deposit" does not match inside "deposits".
  _regex = new RegExp(`(?<![A-Za-z0-9_])(${escaped.join('|')})(?![A-Za-z0-9_])`, 'gi');
  return { terms: _sortedTerms, regex: _regex };
}

/** Exact key first, then case-insensitive — the matcher regex is case-insensitive. */
function lookup<T>(map: Record<string, T>, matched: string): T | undefined {
  const exact = map[matched];
  if (exact !== undefined) return exact;
  const lower = matched.toLowerCase();
  for (const [key, value] of Object.entries(map)) {
    if (key.toLowerCase() === lower) return value;
  }
  return undefined;
}

/** Split text into plain + jargon segments (longest match wins). */
export function annotateJargon(text: string): Segment[] {
  if (!text) return [];
  const { regex } = getMatcher();
  const parts = text.split(regex);
  const out: Segment[] = [];
  for (const part of parts) {
    if (!part) continue;
    const explainer = lookup(DEMO_JARGON, part);
    if (explainer) out.push({ text: part, explainer, locate: lookup(JARGON_LOCATIONS, part) });
    else out.push({ text: part });
  }
  return out;
}

/**
 * Renders guide copy with dotted jargon terms.
 * Hover (or focus) shows a short explainer popover.
 */
export const JargonText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const segments = annotateJargon(text);
  return (
    <span className={className}>
      {segments.map((seg, i) =>
        seg.explainer ? (
          <JargonTerm key={`${i}-${seg.text}`} term={seg.text} explainer={seg.explainer} locate={seg.locate} />
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </span>
  );
};

const JargonTerm: React.FC<{ term: string; explainer: string; locate?: UiLocationKey }> = ({
  term,
  explainer,
  locate,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const tipId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  const positionTip = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tipH = tipRef.current?.offsetHeight ?? 72;
    const tipW = tipRef.current?.offsetWidth ?? 224;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < tipH + gap && rect.top > spaceBelow;
    let top = placeAbove ? rect.top - tipH - gap : rect.bottom + gap;
    top = Math.min(window.innerHeight - tipH - 8, Math.max(8, top));
    const left = Math.min(
      window.innerWidth - tipW / 2 - 8,
      Math.max(tipW / 2 + 8, rect.left + rect.width / 2),
    );
    setCoords({ top, left });
  }, []);

  const show = useCallback(() => setOpen(true), []);
  const hide = useCallback(() => {
    setOpen(false);
    setCoords(null);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    positionTip();
    const onScrollOrResize = () => positionTip();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, positionTip]);

  return (
    <span
      ref={wrapRef}
      className="relative inline"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span
        tabIndex={0}
        role={locate ? 'button' : 'term'}
        aria-label={locate ? `Show me where ${term} is` : undefined}
        aria-describedby={open ? tipId : undefined}
        onClick={locate ? () => locateUi(locate) : undefined}
        onKeyDown={locate ? (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          locateUi(locate);
        } : undefined}
        className={`border-b border-dotted border-primary/70 text-foreground outline-none focus-visible:ring-1 focus-visible:ring-primary/50 rounded-sm ${
          locate ? 'cursor-pointer hover:text-primary hover:border-solid' : 'cursor-help'
        }`}
      >
        {term}
      </span>
      {open &&
        createPortal(
          <span
            ref={tipRef}
            id={tipId}
            role="tooltip"
            className="fixed z-[9999] w-56 max-w-[min(14rem,calc(100vw-1rem))] pointer-events-none"
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              transform: 'translateX(-50%)',
              visibility: coords ? 'visible' : 'hidden',
            }}
          >
            <span className="block bg-popover text-popover-foreground text-[11px] leading-snug p-2.5 rounded-md shadow-lg border border-border text-left font-normal normal-case tracking-normal">
              {explainer}
              {locate && (
                <span className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-primary">
                  <Crosshair className="w-3 h-3 shrink-0" aria-hidden />
                  Click to show me where this is
                </span>
              )}
            </span>
          </span>,
          document.body,
        )}
    </span>
  );
};
